import { useState, FormEvent, useEffect } from "react";
import { useLocation } from "wouter";
import { AppLayout } from "@/components/layout/AppLayout";
import { apiFetch } from "@/lib/api-fetch";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription,
  CardFooter
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { DocumentType, Folder } from "@/types";
import { FileText, Folder as FolderIcon, BookOpen, FileCode } from "lucide-react";
import { useSettings } from "@/contexts/SettingsContext";

export default function CreateDocumentPage() {
  const { openSettings } = useSettings();
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState("");
  const [folder, setFolder] = useState("");
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, navigate] = useLocation();

  // Fetch document types and folders using the new apiFetch utility
  useEffect(() => {
    const fetchDocumentTypes = async () => {
      try {
        console.log('Fetching document types');
        const data = await apiFetch.get<DocumentType[]>("/api/document-types");
        
        console.log('Document types loaded:', data.length);
        setDocumentTypes(data);
        
        if (data.length > 0) {
          setDocType(data[0].name);
        } else {
          // If no document types exist, set error message
          setError("No document types available. Please create at least one document type in Settings.");
        }
      } catch (error) {
        console.error("Error fetching document types:", error);
        setError(error instanceof Error ? error.message : "Unable to load document types. Please try again later.");
      }
    };

    const fetchFolders = async () => {
      try {
        console.log('Fetching folders');
        const data = await apiFetch.get<Folder[]>("/api/folders");
        
        console.log('Folders loaded:', data.length);
        setFolders(data);
      } catch (error) {
        console.error("Error fetching folders:", error);
        // We don't set an error for folders since they're optional
      }
    };
    
    fetchDocumentTypes();
    fetchFolders();
  }, [navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('Creating document');
      
      const payload = {
        title,
        doc_type: docType,
        folder_id: folder && folder !== "none" ? parseInt(folder) : null,
        content: ""
      };
      
      console.log('Document payload:', payload);
      
      // Use our new apiFetch utility which automatically handles auth
      const data = await apiFetch.post<{ id: number }>("/api/documents", payload);
      
      console.log('Document created successfully:', data);
      navigate(`/documents/${data.id}/edit`);
    } catch (error) {
      console.error("Error creating document:", error);
      setError(error instanceof Error ? error.message : "Failed to create document");
    } finally {
      setIsLoading(false);
    }
  };

  const getDocumentTypeIcon = (type: string) => {
    switch (type) {
      case "Sermon":
        return <BookOpen className="h-5 w-5" />;
      default:
        return <FileCode className="h-5 w-5" />;
    }
  };

  return (
    <AppLayout>
      <div className="mx-auto w-full max-w-2xl py-8 px-6">
        <h1 className="text-3xl font-bold mb-8 text-center">Create New Document</h1>
        
        <Card>
          <CardHeader>
            <CardTitle>Document Details</CardTitle>
            <CardDescription>
              Enter the details for your new document
            </CardDescription>
          </CardHeader>
          
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {error && (
                <div className="bg-destructive/10 text-destructive p-4 rounded-md space-y-2">
                  <p>{error}</p>
                  {error.includes("document type") && (
                    <Button 
                      size="sm" 
                      onClick={() => openSettings("document-types")}
                    >
                      Go to Document Type Settings
                    </Button>
                  )}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="title">Document Title</Label>
                <Input 
                  id="title" 
                  placeholder="Enter document title" 
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="type">Document Type</Label>
                <Select 
                  value={docType} 
                  onValueChange={setDocType}
                  required
                >
                  <SelectTrigger id="type">
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {documentTypes.map((type) => (
                      <SelectItem key={type.id} value={type.name}>
                        <div className="flex items-center">
                          {getDocumentTypeIcon(type.name)}
                          <span className="ml-2">{type.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="folder">Folder (Optional)</Label>
                <Select 
                  value={folder} 
                  onValueChange={setFolder}
                >
                  <SelectTrigger id="folder">
                    <SelectValue placeholder="Select a folder (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <span className="text-muted-foreground">No Folder</span>
                    </SelectItem>
                    {folders.map((folder) => (
                      <SelectItem key={folder.id} value={folder.id.toString()}>
                        <div className="flex items-center">
                          <FolderIcon className="h-4 w-4 mr-2" />
                          {folder.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
            
            <CardFooter className="flex justify-between">
              <Button 
                type="button" 
                variant="outline"
                onClick={() => navigate("/")}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-t-2 border-white rounded-full animate-spin mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Create Document
                  </>
                )}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </AppLayout>
  );
}
